import { Component, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatDrawer, MatDrawerContainer, MatSidenav } from '@angular/material/sidenav';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subject } from 'rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { catchError, finalize, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ConfigService, GetBusinessEntities, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { StripeApiService } from '@aiwyn/api';
import { Store } from '@ngxs/store';
import {
  AiwynBreakpoint,
  getFirstOfQueryList,
  getLoadedResults,
  ignoreFalsy,
  ignoreNullish,
  invert,
  isEmpty,
  mapToAndResult,
} from '@aiwyn/shared';
import { GetPaymentPlans } from '../settings/state';
import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonSettingsState, GetAutoPayClients } from '@aiwyn/common/settings';
import { GetPayables, PaymentsState } from '@aiwyn/common/payments';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_REVIEW_AND_PAY } from '../../../routing-constants';

@Component({
  selector: 'app-invoices',
  templateUrl: './invoices.component.html',
  styleUrls: ['./invoices.component.scss'],
  standalone: false,
})
export class InvoicesComponent implements OnDestroy {
  private _unsubscribe$: Subject<void> = new Subject<void>();

  // Sort for material table.
  @ViewChild(MatSort) sort: MatSort;

  @ViewChild('sidenav') public sidenav: MatSidenav;

  // ClientId used to store URL data.
  // Note: URL must use format specified in app-routing.module.ts.
  clientId: string = '';
  hideComponent = true;

  // Data source for material table.
  dataSource = new MatTableDataSource();

  // Columns for material table.
  columnsToDisplay = ['checked', 'clientName', 'invoiceAmount', 'invoiceDescription', 'workCompletedDate', 'invoiceDueDate', 'status'];

  // Search string. Search applies to both tables: unpaid and paid.
  searchString: string;

  // Hide search bar if there are no invoices to show.
  arePayablesEmpty$ = getLoadedResults(this.store.select(PaymentsState.getRawPayablesLoadable)).pipe(
    map(payables => isEmpty(payables)),
  );

  selectedPayablesCount$ = this.store.select(PaymentsState.getArTranAllocations).pipe(
    map(payables => payables?.length ?? 0),
    shareReplay(1),
  );

  shouldShowInvoicesTableSummary$ = defer(() => combineLatest([
    this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES),
    this.isMobile$.pipe(invert()),
    this.store.select(CommonSettingsState.doMultipleClientCurrenciesExist).pipe(
      ignoreNullish(),
      invert(),
    ),
  ])).pipe(
    mapToAndResult(),
  );

  // A client with a net credit balance and zero outstanding invoices still needs to see the
  // "Available Credits" banner (FIRM-11890) — the invoice-only empty state must not hide it.
  hasCredits$ = getLoadedResults(this.store.select(PaymentsState.getCreditsLoadable)).pipe(
    map(credits => !isEmpty(credits)),
  );

  showInvoicesTableSummaryInEmptyState$ = combineLatest([
    this.shouldShowInvoicesTableSummary$,
    this.hasCredits$,
  ]).pipe(
    mapToAndResult(),
  );

  //#region Sidebar

  @ViewChild(MatDrawerContainer) matDrawerContainer: MatDrawerContainer;
  @ViewChildren(MatDrawer) matDrawer: QueryList<MatDrawer>;
  isMobile$: Observable<boolean> = this.breakpointObserver.observe([AiwynBreakpoint.SIDEBAR_POPOVER]).pipe(
    map(breakpointMatched => breakpointMatched.matches),
    shareReplay(1),
  );

  sideDrawerMode$: Observable<'over' | 'side'> = this.isMobile$.pipe(
    map(isMobile => isMobile ? 'over' : 'side'),
  );

  readonly isSideDrawerOpen$ = new BehaviorSubject<boolean>(undefined);
  private _setIsSideDrawerOpen$ = defer(() => getFirstOfQueryList(this.matDrawer).pipe(
    switchMap(drawer => drawer._animationStarted.pipe(
      map(_ => drawer.opened),
    )),
    tap(isOpened => this.isSideDrawerOpen$.next(isOpened)),
    takeUntil(this._unsubscribe$),
  ));

  openSidebar(): void {
    this.matDrawerContainer.open();
  }

  onSidebarOpenedChange(isOpened: boolean): void {
    if (isOpened) {
      this.matDrawerContainer.open();
    } else {
      this.matDrawerContainer.close();
    }
  }

  //#endregion

  constructor(
    public dialog: MatDialog,
    public configService: ConfigService,
    private stripeApiService: StripeApiService,
    private spinner: NgxSpinnerService,
    private store: Store,
    private readonly router: Router,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly launchDarklyService: LaunchDarklyService,
  ) {
    this.spinner.show();

    this.configService.configValuesLoaded$.pipe(
      ignoreFalsy(),
      switchMap(_ => this.store.dispatch([new GetAutoPayClients(), new GetPaymentPlans()])),
      take(1),
      takeUntil(this._unsubscribe$),
    ).subscribe();

    this.store.dispatch([
      new GetPayables(true),
      new GetBusinessEntities(defer(() => this.stripeApiService.getBusinessEntities())),
    ]).pipe(
      catchError((error) => {
        console.error('Failed to load invoices/payment methods', error);
        return of(null);
      }),
      finalize(() => {
        this.hideComponent = false;
        this.spinner.hide();
      }),
      take(1),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  /**
   * Sort for material table.
   */
  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this._setIsSideDrawerOpen$.subscribe();
  }

  /**
   * Multi-table search string.
   */
  getSearchString(filterValue: string) {
    this.searchString = filterValue;
  }

  onPayWithoutInvoice(): void {
    this.matDrawerContainer.open();
  }

  onNavigateToReviewAndPay(): void {
    const selectedPayableIds = this.store.selectSnapshot(PaymentsState.getSelectedPayableIds);
    this.router.navigate(
      [ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_REVIEW_AND_PAY],
      { queryParams: { invoiceId: selectedPayableIds.join(',') } },
    );
  }

  /**
   * Destroy subscriptions.
   */
  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
